import { Spacing } from '@persona-id/design-tokens';
import { ClipboardIcon } from '@persona-id/design-tokens/icons';
import { produce } from 'immer';
import { useCallback } from 'react';
import styled from 'styled-components';

import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import { isTerminalStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import SectionAccordionCondensed from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordionCondensed';
import {
  NavBarLogoStylingSection,
  ScreenStylingSection,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenSectionsPanel/sections';
import {
  cancelButtonEnabledForStep,
  isHeaderComponentVisible,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import ProductUpsellBanner from 'dashboard/apps/InquiryTemplateEditor/ProductUpsellBanner';
import { getValidStepStatus } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { Option } from 'dashboard/components/FormControls/SelectField';
import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import ProductUpsellTooltip, {
  getProductUpsellLevel,
} from 'dashboard/components/ProductUpsellTooltip';
import ScrollablePanelWithFooter from 'dashboard/components/ScrollablePanelWithFooter';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { InquiryStatus } from 'dashboard/models/inquiry';
import {
  Feature,
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepIntegration,
  InquiryTemplateVersionStepUi,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import Button from 'design-system/components/Button';
import Select from 'design-system/components/Select';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { UiComponentConfig } from 'lib/interfaces/ui-components';
import { ThemeAttributes } from 'verify/models/theme';

const BannerWrapper = styled.div`
  margin: ${Spacing[5]} ${Spacing[4]} ${Spacing[4]};
`;

interface ScreenSectionsEditorProps {
  step: InquiryTemplateVersionStepUi | InquiryTemplateVersionStepIntegration;
  stepPreview: StepPreview;
  basePath: string;
  availableBackSteps: InquiryTemplateVersionStep[];
  theme?: ThemeAttributes;
  editable: boolean;
  handleComponentPaste?: () => void;
  handleClosePanel: () => void;
  updateSteps: ({
    updatedStep,
    component,
  }: {
    updatedStep: InquiryTemplateVersionStep;
    component?: UiComponentConfig;
  }) => void;
}

export const ScreenSectionsEditor = ({
  step,
  stepPreview,
  basePath,
  availableBackSteps,
  theme,
  editable,
  handleComponentPaste,
  handleClosePanel,
  updateSteps,
}: ScreenSectionsEditorProps) => {
  const editableForFree =
    useIsEditableWithOptions({
      accessTier: EditorAccessTier.InquiriesFree,
    }) && editable;

  const editableForBasic =
    useIsEditableWithOptions({
      accessTier: EditorAccessTier.InquiriesBasic,
    }) && editable;

  const editableForEnhanced =
    useIsEditableWithOptions({
      accessTier: EditorAccessTier.InquiriesEnhanced,
    }) && editable;

  const hasProductFeature = editableForFree || editableForBasic || editableForEnhanced;

  const footerContent = (
    <>
      <Button
        variant='basic'
        width='auto'
        id='component-panel__close-button'
        onClick={handleClosePanel}
      >
        Close
      </Button>
      {editable && handleComponentPaste && (
        <Button
          title='Paste'
          width='icon'
          variant='basic'
          id='component-panel__paste-button'
          onClick={handleComponentPaste}
        >
          <ClipboardIcon />
        </Button>
      )}
    </>
  );

  return (
    <ScrollablePanelWithFooter footerContent={footerContent}>
      {!hasProductFeature && (
        <BannerWrapper>
          <ProductUpsellBanner
            size='md'
            message='Contact sales to upgrade to the Starter plan and access screen configurations.'
            accessTier={EditorAccessTier.InquiriesFree}
            learnMoreId='screen-configs-upsell'
          />
        </BannerWrapper>
      )}
      <StepStatusSection step={step} />
      <ScreenHeaderSection
        step={step}
        availableBackSteps={availableBackSteps}
        editable={editableForFree}
        updateSteps={updateSteps}
      />
      <SectionAccordionCondensed
        title='Header logo'
        icon={
          hasProductFeature &&
          !editableForEnhanced && (
            <ScreenStyleUpsellBanner
              accessTier={EditorAccessTier.InquiriesEnhanced}
              section='logo'
              feature={Feature.HeaderLogo}
            />
          )
        }
      >
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <NavBarLogoStylingSection disabled={!editableForEnhanced} theme={theme} />
      </SectionAccordionCondensed>
      {isUiStepConfig(step) && (
        <>
          <SectionAccordionCondensed
            title='Styling'
            icon={
              hasProductFeature &&
              !editableForBasic && (
                <ScreenStyleUpsellBanner
                  accessTier={EditorAccessTier.InquiriesBasic}
                  section='styles'
                  feature={Feature.HeaderLogo}
                />
              )
            }
          >
            <ScreenStylingSection
              stepPreview={stepPreview}
              basePath={basePath}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              showHeaderStyleOptions={isHeaderComponentVisible(step, theme['logo-url'])}
              editable={editableForBasic}
            />
          </SectionAccordionCondensed>
        </>
      )}
    </ScrollablePanelWithFooter>
  );
};

interface StepStatusSectionProps {
  step:
    | InquiryTemplateVersionStepUi
    | InquiryTemplateVersionCombinedStep
    | InquiryTemplateVersionStepIntegration;
}

const StepStatusSection = ({ step }: StepStatusSectionProps) => {
  const stepStatus = getValidStepStatus(
    step.config.statuses.map((status) => status.name as InquiryStatus),
  );

  return (
    <>
      {stepStatus && (
        <SectionAccordionCondensed title='Status'>
          <Paragraph>
            Upon reaching this screen in the inquiry flow, the status of the inquiry will transition
            to <InquiryStatusSticker status={stepStatus} />
          </Paragraph>
        </SectionAccordionCondensed>
      )}
    </>
  );
};

const SectionContainer = styled.div`
  width: 100%;
  & + & {
    margin-top: ${Spacing[3]};
  }
`;

const BackStepLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

const SectionToggleContainer = styled(SectionContainer)`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  ${Paragraph} {
    flex-grow: 1;
  }
`;

const LabelHelpContainer = styled.div`
  display: flex;
  align-items: baseline;
`;

interface ScreenHeaderSectionProps {
  step:
    | InquiryTemplateVersionStepUi
    | InquiryTemplateVersionCombinedStep
    | InquiryTemplateVersionStepIntegration;
  availableBackSteps: InquiryTemplateVersionStep[];
  editable: boolean;
  updateSteps: ({
    updatedStep,
    component,
  }: {
    updatedStep: InquiryTemplateVersionStep;
    component?: UiComponentConfig;
  }) => void;
}

export const ScreenHeaderSection = ({
  step,
  availableBackSteps,
  editable,
  updateSteps,
}: ScreenHeaderSectionProps) => {
  const isTerminal = isTerminalStep(step);

  const onBackStepChange = useCallback(
    (backStepTarget: string) => {
      const updatedStep = produce(step, (draftStep) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        draftStep.config['back-step'] = backStepTarget === 'null' ? null : backStepTarget;
      });
      updateSteps({ updatedStep });
    },
    [step, updateSteps],
  );

  const onCloseButtonToggle = useCallback(
    (toggleOn: boolean) => {
      const updatedStep = produce(step, (draftStep: InquiryTemplateVersionStepUi) => {
        if (isUiStepConfig(step)) {
          draftStep.config['hide-cancel-button'] = !toggleOn;
        }
      });
      updateSteps({ updatedStep });
    },
    [step, updateSteps],
  );

  return (
    <SectionAccordionCondensed title='Header'>
      {/** Hide back-step configuration for initial screens */}
      {!step.config.statuses.some((status) => status.initial) && (
        <SectionContainer>
          <BackStepLabel color={!editable ? 'disabled' : 'primary'} htmlFor='back-step-select'>
            Back step
          </BackStepLabel>
          <Select
            key={step.config['back-step']}
            onValueChange={onBackStepChange}
            size='sm'
            disabled={!editable}
            id='back-step-select'
            placeholder='Select a step to navigate back to'
            value={step.config['back-step']}
          >
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <Option value={null}>No back step</Option>
            {availableBackSteps.map((availableBackStep, idx) => (
              <Option key={`${availableBackStep.name}-${idx}`} value={availableBackStep.name}>
                {getDisplayNameForStep(availableBackStep)}
              </Option>
            ))}
          </Select>
        </SectionContainer>
      )}
      {isUiStepConfig(step) && (
        <SectionToggleContainer>
          <LabelHelpContainer>
            <Paragraph color={!editable || isTerminal ? 'disabled' : 'primary'}>
              Show close button
            </Paragraph>
            <HelpIcon>
              {isTerminal
                ? 'The close button cannot be displayed on a terminal step'
                : 'The close button is never displayed for hosted or inline flows'}
            </HelpIcon>
          </LabelHelpContainer>
          <ToggleSlider
            value={isTerminal ? false : cancelButtonEnabledForStep(step)}
            disabled={!editable || isTerminal}
            onChange={onCloseButtonToggle}
          />
        </SectionToggleContainer>
      )}
    </SectionAccordionCondensed>
  );
};

const ScreenStyleUpsellBanner = ({
  accessTier,
  section,
  feature,
}: {
  accessTier: EditorAccessTier;
  section: string;
  feature?: Feature;
}) => {
  const productLevel = getProductUpsellLevel(accessTier);
  return (
    <ProductUpsellTooltip
      side='left'
      learnMoreId={`screen-config-${section}`}
      productUpsellLevel={productLevel}
      feature={feature}
      accessTier={accessTier}
      trigger={
        <InquiriesGatingSticker
          dataMarketingIdSuffix={`screen-configs-${section}`}
          stickerType={EnhancedGatingStickerType.Limited}
          iconOnly
        />
      }
    />
  );
};
